import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Lock } from "lucide-react";

const API = "/api";
const STORAGE_KEY = "evoluras.milestones.achieved";

/**
 * Compact badge row showing circle milestones.
 * Toasts when a new milestone is unlocked (compared to localStorage snapshot).
 */
const MilestoneBadges = ({ refreshKey }) => {
  const [data, setData] = useState(null);
  const prevAchievedRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const res = await fetch(`${API}/network/milestones`, { credentials: "include" });
        if (!res.ok) return;
        const d = await res.json();
        if (cancelled) return;
        setData(d);

        // Celebrate newly-achieved milestones
        const achievedIds = d.milestones.filter((m) => m.achieved).map((m) => m.id);
        let previouslySeen;
        try {
          previouslySeen = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
        } catch {
          previouslySeen = [];
        }
        // Skip celebration on very first load (no baseline yet)
        if (prevAchievedRef.current !== null || previouslySeen.length > 0) {
          const baseline = new Set(previouslySeen);
          const newlyUnlocked = d.milestones.filter(
            (m) => m.achieved && !baseline.has(m.id)
          );
          newlyUnlocked.forEach((m) => {
            toast.success(`${m.emoji} ${m.label} unlocked!`, {
              description: m.description,
              duration: 5000,
            });
          });
        }
        prevAchievedRef.current = achievedIds;
        localStorage.setItem(STORAGE_KEY, JSON.stringify(achievedIds));
      } catch {}
    };
    run();
    return () => { cancelled = true; };
  }, [refreshKey]);

  if (!data) return null;

  return (
    <div
      className="flex items-center gap-2 overflow-x-auto py-1 -mx-1 px-1 scrollbar-hide"
      data-testid="milestone-badges"
    >
      {data.milestones.map((m) => {
        const pct = Math.min(100, Math.round((m.progress / m.target) * 100));
        return (
          <div
            key={m.id}
            className={`flex-shrink-0 flex items-center gap-1.5 pl-2 pr-2.5 py-1 rounded-full text-[11px] font-medium border transition-all ${
              m.achieved
                ? "bg-gradient-to-r from-violet-50 to-indigo-50 text-violet-700 border-violet-200/70 shadow-sm shadow-violet-500/10"
                : "bg-slate-50 text-slate-500 border-slate-200/70"
            }`}
            data-testid={`milestone-${m.id}`}
            title={
              m.achieved
                ? `${m.label} — achieved! ${m.description}`
                : `${m.label} — ${m.progress}/${m.target}. ${m.description}`
            }
          >
            <span className={m.achieved ? "text-base leading-none" : "text-slate-400"}>
              {m.achieved ? m.emoji : <Lock className="w-3 h-3" />}
            </span>
            <span className="leading-none">{m.label}</span>
            {!m.achieved && (
              <span className="text-[10px] text-slate-400 font-normal">
                {m.progress}/{m.target}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default MilestoneBadges;
